import React, { useMemo, useState } from 'react';
import useAuth from '../hooks/utils/useAuth';
import { useHubspotEventMutation } from '../hooks/api/useEvent';
import { useProviderDetails } from './modals/resource-modal/useProviderDetails';
import { TopicTag } from './topic-tag';
import { ResourceModal } from './modals/resource-modal/resource-modal';
import { Resource as ResourceFromResources } from '../hooks/api/useResources';
import { Resource as ResourceFromExperiences } from '../hooks/api/useExperiences';
import { ProviderResource } from '../hooks/utils/useProviderResources';
import { useTagGroups } from '../hooks/api/useTagGroups';
import { ExperienceMatchHint, MatchStyleVariant } from './peer-listener-match-hint';

// Union type that accepts either Resource type, with optional properties for compatibility
type Resource = (ResourceFromResources | ResourceFromExperiences) & {
  tag_group_names?: string[];
  rating?: number;
};

const getDisplayTagGroup = (tagGroupNames: string[], selectedTagGroupName?: string): string | undefined => {
  if (!tagGroupNames?.length) {
    return undefined;
  }
  if (selectedTagGroupName && tagGroupNames.includes(selectedTagGroupName)) {
    return selectedTagGroupName;
  }
  return tagGroupNames[0];
};

interface ResourceTileProps {
  id?: number;
  image?: string;
  resourceName?: string;
  caption?: string;
  tagGroupNames?: string[];
  selectedTagGroupName?: string;
  isGlobal?: boolean;
  providerPath?: string;
  isLoading?: boolean;
  resource?: Resource;
  providerResource?: ProviderResource;
  centered?: boolean;
  /** Peersona chat uses a smaller tile so four fit across the panel. */
  compact?: boolean;
  /** Fired when the user opens the resource modal from this tile. */
  onTileEngage?: () => void;
  matchStyle?: MatchStyleVariant;
}

export const ResourceTileSkeleton: React.FC<{ compact?: boolean }> = ({ compact = false }) => {
  if (compact) {
    return (
      <div className="relative flex w-full flex-col items-start gap-1.5">
        <div className="relative w-full pt-[100%]">
          <div className="absolute inset-0">
            <div className="absolute left-[2px] top-[2px] h-[calc(100%-4px)] w-[calc(100%-4px)] rotate-[3deg] animate-pulse rounded-md bg-gray-200" />
            <div className="absolute inset-0 top-1 animate-pulse rounded-md bg-gray-200" />
          </div>
        </div>
        <div className="h-4 w-full animate-pulse rounded bg-gray-200" />
      </div>
    );
  }

  return (
    <div className="relative flex w-[194px] flex-col items-start gap-3">
      <div className="relative h-[194px] w-[194px]">
        <div className="absolute left-[5px] top-[5px] h-[183px] w-[183px] rotate-[3.61deg] animate-pulse rounded-xl bg-gray-200" />
        <div className="absolute left-0 top-4 h-[185px] w-[185px] animate-pulse rounded-xl bg-gray-200" />
      </div>
      <div className="h-6 w-28 animate-pulse rounded bg-gray-200" />
      <div className="h-5 w-full animate-pulse rounded bg-gray-200" />
    </div>
  );
};

export const ResourceTile: React.FC<ResourceTileProps> = ({
  image,
  resourceName,
  tagGroupNames,
  selectedTagGroupName,
  isGlobal,
  isLoading = false,
  resource: resourceProp,
  providerResource,
  centered = false,
  compact = false,
  onTileEngage,
  matchStyle = 'none',
}) => {
  const { user } = useAuth();
  const { providerName } = useProviderDetails();
  const hubspotMutation = useHubspotEventMutation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { data: topicTagGroups } = useTagGroups();

  const displayImage = image || resourceProp?.image_url || resourceProp?.wide_image_url;
  const resolvedTagGroupNames = tagGroupNames || resourceProp?.tag_group_names || resourceProp?.tag_names;
  const resolvedResourceName = resourceName || resourceProp?.name;

  const displayTagGroup = useMemo(
    () => getDisplayTagGroup(resolvedTagGroupNames || [], selectedTagGroupName),
    [resolvedTagGroupNames, selectedTagGroupName],
  );

  if (isLoading) {
    return <ResourceTileSkeleton compact={compact} />;
  }

  const tileWidthClass = compact ? 'w-full' : 'w-[194px]';
  const titleClass = compact
    ? `font-open-sans line-clamp-2 w-full text-xs font-semibold leading-4 text-black ${centered ? 'text-center' : ''}`
    : `font-open-sans w-full text-base font-semibold leading-[20px] text-black ${centered ? 'text-center' : ''}`;

  const handleHubspotClickResourceEvent = () => {
    providerResource
      ? hubspotMutation.mutate({
          event: 'click_provider',
          properties: {
            email_address: user?.email_address!,
            package_code: user?.caller_role.active_subscription?.package.code!,
            provider_name: providerName || '',
            provider_plan_name: resourceName || '',
          },
        })
      : hubspotMutation.mutate({
          event: isGlobal ? 'click_global_resource' : 'click_client_resource',
          properties: {
            email_address: user?.email_address!,
            package_code: user?.caller_role.active_subscription?.package.code!,
            resource_name: resourceName || '',
            in_search_results: false,
            resource_event_location: 'browse_resources',
          },
        });
  };

  const handleOpenModal = () => {
    onTileEngage?.();
    if (user) {
      handleHubspotClickResourceEvent();
    }
    setIsModalOpen(true);
  };

  return (
    <>
      <div
        className={`relative flex ${tileWidthClass} flex-col ${compact ? 'gap-1.5' : 'gap-3'} ${centered ? 'items-center' : 'items-start'}`}
      >
        {compact ? (
          <div className="relative w-full pt-[100%]">
            <button
              type="button"
              onClick={handleOpenModal}
              className="absolute inset-0 cursor-pointer"
              aria-label={`View ${resolvedResourceName || 'resource'} details`}
            >
              <div className="absolute left-[2px] top-[2px] h-[calc(100%-4px)] w-[calc(100%-4px)] rotate-[3deg] rounded-md bg-[rgba(114,117,136,0.15)]" />
              <div className="border-primary absolute inset-0 top-1 overflow-hidden rounded-md border bg-slate-100">
                {displayImage ? (
                  <img
                    src={displayImage}
                    alt={resolvedResourceName || 'Resource'}
                    className="pointer-events-none h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <p className="font-open-sans px-1 text-center text-[10px] font-normal leading-tight text-gray-500">
                      no image found
                    </p>
                  </div>
                )}
              </div>
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={handleOpenModal}
            className="relative h-[194px] w-[194px] cursor-pointer"
            aria-label={`View ${resolvedResourceName || 'resource'} details`}
          >
            <div className="absolute left-[5px] top-[5px] h-[183px] w-[183px] rotate-[3.61deg] rounded-xl bg-[rgba(114,117,136,0.15)]" />
            <div className="border-primary absolute left-0 top-4 h-[185px] w-[185px] overflow-hidden rounded-xl border bg-slate-100">
              {displayImage ? (
                <img
                  src={displayImage}
                  alt={resolvedResourceName || 'Resource'}
                  className="pointer-events-none h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center">
                  <p className="font-open-sans text-sm font-normal leading-[1.5] text-gray-500">no image found</p>
                </div>
              )}
            </div>
          </button>
        )}

        {displayTagGroup && !centered && !compact ? (
          <TopicTag tagGroupName={displayTagGroup} tagGroups={topicTagGroups} />
        ) : null}

        <p className={titleClass}>{resolvedResourceName}</p>
        {resourceProp ? <ExperienceMatchHint match={resourceProp} variant={matchStyle} /> : null}
      </div>
      {isModalOpen && (resourceProp || providerResource) && (
        <ResourceModal
          resource={resourceProp}
          providerResource={providerResource}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </>
  );
};
